/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { Fragment, ReactElement } from "react";

import "./TravelExpensesTable.css";
import { styled } from "@mui/material";
import Collapse from "@mui/material/Collapse";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import { Button as BulmaButton } from "react-bulma-components";

import { CommutingRouteDialog } from "components/";
import { IconQuestion, IconExclamation } from "components/atoms/Icon";
import { CommutingRouteProp } from "components/CommutingRouteDialog/CommutingRouteDialog";
import { ApplicationStatus } from "components/data-display/ApplicationStatus";
import { DateButton } from "components/inputs/DateButton";
import { PriceLabel } from "components/PriceLabel/PriceLabel";
import { TransitsTable } from "components/TransitsTable/TransitsTable";
import { TransportationExpenseDialog } from "components/TransportationExpenseDialog/TransportationExpenseDialog";
import { WorkplaceDialog } from "components/WorkplaceDialog/WorkplaceDialog";
import { getDistanceKmBracketsString } from "helpers/distance";
import Address from "models/address";
import { NewMovementLog } from "models/movementLog";
import { PreparedRoute } from "models/preparedRoute";
import { TransportationExpense } from "models/transportationExpense";
import { CommutingExpense, DaySummary } from "models/travelExpense";

const StyledTableHeadCell = styled(TableCell)({
  border: "1px solid hsl(0, 0%, 86%)",
  backgroundColor: "hsl(219, 70%, 96%)",
});

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  border: "1px solid hsl(0, 0%, 86%)",
  padding: theme.spacing(1),
}));

// eslint-disable-next-line consistent-return
export const renderWorkStatus = (daySummary: DaySummary) => {
  if (daySummary.workStatus === "work") {
    return "出勤";
  }
  if (daySummary.workStatus === "rest") {
    return "休日";
  }
};

export const renderDestinations = (daySummary: DaySummary) =>
  daySummary.commutingExpenses.reduce((acc: string[], commutingExpense) => {
    if (commutingExpense.arrivalName) {
      return [...acc, commutingExpense.arrivalName];
    }
    return acc;
  }, []);

export const renderTotalAmountOfCommuting = (daySummary: DaySummary) =>
  daySummary.commutingExpenses.reduce(
    (acc: number, commutingExpense) => acc + commutingExpense.totalFare,
    0
  );

export const renderTotalAmountOfTransportation = (daySummary: DaySummary) =>
  daySummary.transportationExpenses.reduce(
    (acc: number, transportationExpense) =>
      acc + transportationExpense.totalFare,
    0
  );

const renderTransportationExpenseRoute = (
  transportationExpense: TransportationExpense,
  isEditableApplicationStatus: boolean,
  handleMovementLogDelete: (id: number) => void
) => (
  <div key={transportationExpense.id} className="cw-travel-expense-route">
    <div className="cw-travel-expense-route-meta">
      <div className="cw-travel-expense-route-meta-title">
        <h4 className="title is-4">
          {`${transportationExpense.departureName} → ${transportationExpense.arrivalName}`}
          {transportationExpense.modifiedBy === "user" && <IconExclamation />}
        </h4>
      </div>
      <div className="cw-travel-expense-route-meta-actions">
        <BulmaButton
          disabled={!isEditableApplicationStatus}
          onClick={() => handleMovementLogDelete(transportationExpense.id)}
        >
          削除
        </BulmaButton>
      </div>
    </div>
    <TransitsTable movementRoutes={transportationExpense.movementRoutes} />
  </div>
);

const renderToggleCommutingDialogButton = (
  daySummary: DaySummary,
  isEditableApplicationStatus: boolean,
  isHomeAddressNotLoaded: boolean,
  toggleCommutingRouteDialog: (
    commutingExpense: CommutingExpense,
    hasOutwardPath?: boolean,
    hasReturnPath?: boolean,
    isReturnPath?: boolean
  ) => void
) => {
  const hasOutwardPath = !!daySummary.commutingExpenses.find(
    (commutingExpense) => commutingExpense.departureName === null
  );

  const hasReturnPath = !!daySummary.commutingExpenses.find(
    (commutingExpense) => commutingExpense.arrivalName === null
  );

  return (
    <BulmaButton
      disabled={
        daySummary.commutingExpenses.length > 1 ||
        !isEditableApplicationStatus ||
        isHomeAddressNotLoaded
      }
      onClick={() =>
        toggleCommutingRouteDialog(
          {
            at: daySummary.date,
            arrivalId: 0,
            arrivalName: "",
          } as CommutingExpense,
          hasOutwardPath,
          hasReturnPath,
          undefined
        )
      }
    >
      通勤費追加
    </BulmaButton>
  );
};

const renderCommutingExpenseRoute = (
  commutingExpense: CommutingExpense,
  _commutingRouteProp: CommutingRouteProp,
  isEditableApplicationStatus: boolean,
  isOverCutOffDate: boolean,
  toggleCommutingRouteDialog: (
    commutingExpense_: CommutingExpense,
    hasOutwardPath?: boolean,
    hasReturnPath?: boolean,
    isReturnpath?: boolean
  ) => void,
  _toggleWorkplaceDialog: (commutingRouteProp_: CommutingRouteProp) => void,
  handleMovementLogDelete: (id: number) => void,
  daySummary: DaySummary
) => {
  const arrivalName = commutingExpense.arrivalName
    ? commutingExpense.arrivalName
    : "自宅";
  const departureName = commutingExpense.departureName
    ? commutingExpense.departureName
    : "自宅";

  if (
    commutingExpense.movementRoutes !== undefined &&
    commutingExpense.movementRoutes.length > 0
  ) {
    return (
      <div key={commutingExpense.id} className="cw-travel-expense-route">
        <div className="cw-travel-expense-route-meta">
          <div className="cw-travel-expense-route-meta-title">
            <h4 className="title is-4">{`${departureName} → ${arrivalName}`}</h4>
            <div className="cw-travel-expense-route-meta-title-item">
              {getDistanceKmBracketsString(commutingExpense.totalDistanceKm)}
              <span>
                {
                  // eslint-disable-next-line no-nested-ternary
                  daySummary.applicationStatus === "未申請" ? (
                    <IconQuestion />
                  ) : commutingExpense.modifiedBy === "user" ? (
                    <IconExclamation />
                  ) : null
                }
              </span>
            </div>
          </div>
          <div className="cw-travel-expense-route-meta-actions">
            {!isOverCutOffDate && (
              <>
                <BulmaButton
                  disabled={!isEditableApplicationStatus}
                  onClick={() =>
                    toggleCommutingRouteDialog(
                      commutingExpense,
                      undefined,
                      undefined,
                      !commutingExpense.arrivalName
                    )
                  }
                >
                  経路変更
                </BulmaButton>
                <BulmaButton
                  disabled={!isEditableApplicationStatus}
                  onClick={() => handleMovementLogDelete(commutingExpense.id)}
                >
                  削除
                </BulmaButton>
              </>
            )}
          </div>
        </div>
        <TransitsTable movementRoutes={commutingExpense.movementRoutes} />
      </div>
    );
  }
  return (
    <div key={commutingExpense.id} className="cw-travel-expense-route">
      <div className="cw-travel-expense-route-meta">
        <div className="cw-travel-expense-route-meta-title">
          <h4 className="title is-4">{`${departureName} → ${arrivalName}`}</h4>
          <div className="cw-travel-expense-route-meta-title-item">
            {getDistanceKmBracketsString(commutingExpense.totalDistanceKm)}
          </div>
        </div>
        <div className="cw-travel-expense-route-meta-actions">
          <BulmaButton
            disabled={!isEditableApplicationStatus}
            onClick={() => toggleCommutingRouteDialog(commutingExpense)}
          >
            経路変更
          </BulmaButton>
          <BulmaButton
            disabled={!isEditableApplicationStatus}
            onClick={() => handleMovementLogDelete(commutingExpense.id)}
          >
            削除
          </BulmaButton>
        </div>
      </div>
      <div className="cw-travel-expenses-wrapper">予測経路なし</div>
    </div>
  );
};

const renderTravelExpense = (
  daySummary: DaySummary,
  expandedDaySummaries: string[],
  commutingRouteProp: CommutingRouteProp,
  isEditableApplicationStatus: boolean,
  isHomeAddressNotLoaded: boolean,
  isOverCutOffDate: boolean,
  toggleTransits: (daySummary_: DaySummary) => void,
  toggleCommutingRouteDialog: (
    commutingExpense: CommutingExpense,
    hasOutwardPath?: boolean,
    hasReturnPath?: boolean,
    isReturnPath?: boolean
  ) => void,
  toggleWorkplaceDialog: () => void,
  handleMovementLogDelete: (id: number) => void,
  toggleTransportationExpenseDialog: (daySummary_: DaySummary) => void
) => {
  const open = !!expandedDaySummaries.find(
    (expandedDaySummary) => daySummary.date === expandedDaySummary
  );

  // eslint-disable-next-line no-param-reassign
  daySummary.commutingExpenses = daySummary.commutingExpenses.sort((a, _b) => {
    if (a.departureCode) return 1;
    return -1;
  });

  return (
    <Fragment key={daySummary.date}>
      <TableRow>
        <StyledTableCell style={{ textAlign: "center" }}>
          <DateButton
            daySummary={daySummary}
            onClick={() => toggleTransits(daySummary)}
          />
        </StyledTableCell>
        <StyledTableCell>{renderDestinations(daySummary)}</StyledTableCell>
        <StyledTableCell>{renderWorkStatus(daySummary)}</StyledTableCell>
        <StyledTableCell>
          <PriceLabel amount={renderTotalAmountOfCommuting(daySummary)} />
        </StyledTableCell>
        <StyledTableCell>
          <PriceLabel amount={renderTotalAmountOfTransportation(daySummary)} />
        </StyledTableCell>
        <StyledTableCell>
          <ApplicationStatus daySummary={daySummary} />
        </StyledTableCell>
      </TableRow>
      <TableRow>
        <TableCell sx={{ p: 0 }} colSpan={6}>
          <Collapse in={open} timeout="auto">
            <div className="cw-travel-expense-routes">
              <div className="cw-travel-expense-route-meta">
                <h3 className="title is-3 cw-travel-expense-route-meta-title">
                  移動区分: 通勤費
                </h3>
                <div className="cw-travel-expense-route-meta-actions">
                  {!isOverCutOffDate &&
                    renderToggleCommutingDialogButton(
                      daySummary,
                      isEditableApplicationStatus,
                      isHomeAddressNotLoaded,
                      toggleCommutingRouteDialog
                    )}
                </div>
              </div>
              {daySummary.commutingExpenses.map((commutingExpense) =>
                renderCommutingExpenseRoute(
                  commutingExpense,
                  commutingRouteProp,
                  isEditableApplicationStatus,
                  isOverCutOffDate,
                  toggleCommutingRouteDialog,
                  toggleWorkplaceDialog,
                  handleMovementLogDelete,
                  daySummary
                )
              )}
              <div className="cw-travel-expense-route-transportation-meta">
                <h3 className="title is-3 cw-travel-expense-route-transportation-title">
                  移動区分: 交通費
                </h3>
                <div className="cw-travel-expense-route-transportation-meta-actions">
                  {!isOverCutOffDate && (
                    <BulmaButton
                      disabled={
                        !isEditableApplicationStatus || isHomeAddressNotLoaded
                      }
                      onClick={() =>
                        toggleTransportationExpenseDialog(daySummary)
                      }
                    >
                      交通費追加
                    </BulmaButton>
                  )}
                </div>
              </div>
              {daySummary.transportationExpenses.map((transportationExpense) =>
                renderTransportationExpenseRoute(
                  transportationExpense,
                  isEditableApplicationStatus,
                  handleMovementLogDelete
                )
              )}
            </div>
          </Collapse>
        </TableCell>
      </TableRow>
    </Fragment>
  );
};
const renderTravelExpenses = (
  daySummaries: DaySummary[],
  isMovementLogsLoaded: boolean,
  expandedDaySummaries: string[],
  commutingRouteProp: CommutingRouteProp,
  isEditableApplicationStatus: boolean,
  isHomeAddressNotLoaded: boolean,
  isOverCutOffDate: boolean,
  toggleTransits: (daySummary: DaySummary) => void,
  toggleCommutingRouteDialog: (
    commutingExpense: CommutingExpense,
    hasOutwardPath?: boolean,
    hasReturnPath?: boolean,
    isReturnPath?: boolean
  ) => void,
  toggleWorkplaceDialog: () => void,
  handleMovementLogDelete: (id: number) => void,
  toggleTransportationExpenseDialog: (daySummary: DaySummary) => void
) => {
  if (!isMovementLogsLoaded) {
    return <div className="cw-travel-expenses-wrapper" />;
  }

  if (daySummaries.length > 0) {
    return (
      <div className="cw-travel-expenses-wrapper">
        <div className="cw-travel-expenses-notes">
          <p>
            <IconExclamation />: 手動で変更された経路
          </p>
          <p>
            <IconQuestion />: 未申請の経路
          </p>
        </div>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <StyledTableHeadCell
                  style={{ textAlign: "center" }}
                  sx={{ width: 10 }}
                >
                  日付
                </StyledTableHeadCell>
                <StyledTableHeadCell>通勤・移動先</StyledTableHeadCell>
                <StyledTableHeadCell>勤務ステータス</StyledTableHeadCell>
                <StyledTableHeadCell>通勤費</StyledTableHeadCell>
                <StyledTableHeadCell>交通費</StyledTableHeadCell>
                <StyledTableHeadCell>通勤経路申請状況</StyledTableHeadCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {daySummaries.map((daySummary) =>
                renderTravelExpense(
                  daySummary,
                  expandedDaySummaries,
                  commutingRouteProp,
                  isEditableApplicationStatus,
                  isHomeAddressNotLoaded,
                  isOverCutOffDate,
                  toggleTransits,
                  toggleCommutingRouteDialog,
                  toggleWorkplaceDialog,
                  handleMovementLogDelete,
                  toggleTransportationExpenseDialog
                )
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </div>
    );
  }
  return (
    <div className="cw-travel-expenses-wrapper">
      対象月度内の移動経路情報はありません
    </div>
  );
};

export interface TravelExpensesTableProps {
  daySummaries: DaySummary[];
  isMovementLogsLoaded: boolean;
  expandedDaySummaries: string[];
  toggleTransits: (daySummary: DaySummary) => void;
  isCommutingRouteDialogVisible: boolean;
  isWorkplaceDialogVisible: boolean;
  isEditableApplicationStatus: boolean;
  isHomeAddressNotLoaded: boolean;
  toggleCommutingRouteDialog: (
    commutingExpense?: CommutingExpense,
    hasOutwardPath?: boolean,
    hasReturnPath?: boolean,
    isReturnPath?: boolean
  ) => void;
  registerRoute: (
    route: PreparedRoute,
    at: string,
    isReturnPath: boolean
  ) => void;
  commutingRouteProp: CommutingRouteProp;
  hasOutwardPath: boolean;
  hasReturnPath: boolean;
  isReturnPath: boolean;
  isOverCutOffDate?: boolean;
  toggleWorkplaceDialog: () => void;
  changeWorkplace: (
    commutingRouteProp: CommutingRouteProp,
    workplace: Address
  ) => void;
  handleMovementLogDelete: (id: number) => void;
  isTransportationExpenseDialogVisible: boolean;
  toggleTransportationExpenseDialog: (daySummary: DaySummary) => void;
  transportationExpenseDaySummary: DaySummary;
  registerTransportationExpense: (movementLog: NewMovementLog) => void;
}

export function TravelExpensesTable({
  daySummaries,
  isMovementLogsLoaded,
  expandedDaySummaries,
  toggleTransits,
  isCommutingRouteDialogVisible,
  isWorkplaceDialogVisible,
  isEditableApplicationStatus,
  isHomeAddressNotLoaded,
  toggleCommutingRouteDialog,
  registerRoute,
  commutingRouteProp,
  hasOutwardPath,
  hasReturnPath,
  isReturnPath,
  isOverCutOffDate = false,
  toggleWorkplaceDialog,
  changeWorkplace,
  handleMovementLogDelete,
  isTransportationExpenseDialogVisible,
  toggleTransportationExpenseDialog,
  transportationExpenseDaySummary,
  registerTransportationExpense,
}: TravelExpensesTableProps): ReactElement | null {
  return (
    <>
      {renderTravelExpenses(
        daySummaries,
        isMovementLogsLoaded,
        expandedDaySummaries,
        commutingRouteProp,
        isEditableApplicationStatus,
        isHomeAddressNotLoaded,
        isOverCutOffDate,
        toggleTransits,
        toggleCommutingRouteDialog,
        toggleWorkplaceDialog,
        handleMovementLogDelete,
        toggleTransportationExpenseDialog
      )}
      <CommutingRouteDialog
        toggleDialog={toggleCommutingRouteDialog}
        registerRoute={registerRoute}
        toggleWorkplaceDialog={toggleWorkplaceDialog}
        isVisible={isCommutingRouteDialogVisible}
        commutingRouteProp={commutingRouteProp}
        hasOutwardPath={hasOutwardPath}
        hasReturnPath={hasReturnPath}
        isReturnPath={isReturnPath}
      />
      <WorkplaceDialog
        toggleDialog={toggleWorkplaceDialog}
        changeWorkplace={changeWorkplace}
        isVisible={isWorkplaceDialogVisible}
        commutingRouteProp={commutingRouteProp}
      />
      <TransportationExpenseDialog
        registerTransportationExpense={registerTransportationExpense}
        toggleDialog={toggleTransportationExpenseDialog}
        isVisible={isTransportationExpenseDialogVisible}
        transportationExpenseDaySummary={transportationExpenseDaySummary}
      />
    </>
  );
}
/* eslint-enable */
